import type {
  VendorPayment,
  VendorPaymentFilters,
  VendorPaymentUpdateInput,
} from "@/features/vendor-payments/types";
import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import { orderListPage } from "@/lib/db/list-order";
import { fetchListTotals } from "@/lib/db/list-totals";
import { cast, toPostgrestIlikeContains } from "@/lib/utils/query-helpers";

const backend = getBackendClient();

const VENDOR_PAYMENT_SELECT = `
  id, uid, amount, date, method, reference, purchase_order_id, account_id, created_at,
  gl_accounts!gl_vendor_payments_account_id_fkey ( id, name, uid ),
  gl_purchase_orders!gl_vendor_payments_purchase_order_id_fkey ( id, uid )
`;

export async function getVendorPaymentById(id: string): Promise<VendorPayment> {
  const { data, error } = await backend.data
    .from("gl_vendor_payments")
    .select(VENDOR_PAYMENT_SELECT)
    .eq("id", id)
    .single();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("Vendor payment not found.");
  }

  return cast<VendorPayment>(data);
}

/**
 * Columns the vendor-payment list may be ordered by, as they appear in the
 * URL.
 *
 * Canonical here rather than in the search params because this is the seam that
 * reaches the database: the value ends up in PostgREST's `order` clause, so it
 * is re-checked at the point of use instead of trusting every caller to have
 * sanitized it first.
 */
export const VENDOR_PAYMENT_SORT_FIELDS = [
  "amount",
  "date",
  "method",
  "purchase_order",
  "uid",
  "vendor",
] as const;

export type VendorPaymentSortField = (typeof VENDOR_PAYMENT_SORT_FIELDS)[number];

const DEFAULT_VENDOR_PAYMENT_SORT: VendorPaymentSortField = "date";

/**
 * Vendor and purchase order sort through their embeddings so the order matches
 * the name and uid the cells actually render, not the foreign key's uuid text.
 */
const VENDOR_PAYMENT_ORDER_EXPRESSIONS: Record<VendorPaymentSortField, string> = {
  amount: "amount",
  date: "date",
  method: "method",
  purchase_order: "gl_purchase_orders(uid)",
  uid: "uid",
  vendor: "gl_accounts(name)",
};

/** Anything outside the whitelist falls back to the default order. */
function vendorPaymentOrderExpression(field?: string): string {
  const allowed = VENDOR_PAYMENT_SORT_FIELDS.find((candidate) => candidate === field);
  return VENDOR_PAYMENT_ORDER_EXPRESSIONS[allowed ?? DEFAULT_VENDOR_PAYMENT_SORT];
}

/**
 * The filters the vendor-payment list narrows by, applied in one place so the
 * list and the totals RPC cannot select two different sets.
 *
 * Search matches the `search_text` computed column, which reaches uid,
 * reference, method, amount text, account name/uid and purchase-order uid in
 * one request. The same function backs `vendor_payment_list_totals`, so the
 * footer and the rows are filtered by identical SQL.
 */
function applyVendorPaymentListFilters<T>(
  query: QueryBuilder<T>,
  filters: VendorPaymentFilters,
): QueryBuilder<T> {
  let next = query;

  if (filters.purchase_order_id) {
    next = next.eq("purchase_order_id", filters.purchase_order_id);
  }
  if (filters.account_id) {
    next = next.eq("account_id", filters.account_id);
  }
  if (filters.search?.trim()) {
    const search = toPostgrestIlikeContains(filters.search.trim());
    next = next.or(`search_text.ilike.${search}`);
  }

  return next;
}

/**
 * Lists vendor payments with optional search, account and purchase-order
 * filters.
 *
 * Ordering is a server concern: the list renders one page at a time, so a sort
 * applied after the fetch would only reshuffle that page. `orderListPage`
 * closes the order with the unique tiebreaker a paged read needs — `date` is
 * date-only, so every payment taken on the same day ties.
 */
export async function getVendorPayments(
  filters: VendorPaymentFilters = {},
): Promise<VendorPayment[]> {
  const limit = filters.limit ?? 50;
  const offset = Math.max(0, filters.offset ?? 0);

  let query = orderListPage(backend.data.from("gl_vendor_payments").select(VENDOR_PAYMENT_SELECT), {
    ascending: filters.sort_direction === "asc",
    column: vendorPaymentOrderExpression(filters.sort_field),
  }).range(offset, offset + limit - 1);

  query = applyVendorPaymentListFilters(query, filters);

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }

  return cast<VendorPayment[]>(data || []);
}

/**
 * The filter arguments `vendor_payment_list_totals` takes, built once from the
 * same filter object the list query reads. Page and sort are never inputs:
 * they cannot move a total.
 */
function vendorPaymentTotalsArgs(filters: VendorPaymentFilters): Record<string, unknown> {
  return {
    p_account_id: filters.account_id || null,
    p_purchase_order_id: filters.purchase_order_id || null,
    p_search: filters.search?.trim() || null,
  };
}

/** The aggregate fields `vendor_payment_list_totals` returns. */
const VENDOR_PAYMENT_TOTAL_FIELDS = ["row_count", "total_amount"] as const;

export type VendorPaymentListTotals = Record<(typeof VENDOR_PAYMENT_TOTAL_FIELDS)[number], number>;

/**
 * Count and total paid for the whole filtered set, from the server.
 *
 * Takes the same filters `getVendorPayments` applies, so the strip under the
 * table and the rows in it describe one set — never a sum over the page in
 * view.
 */
export function getVendorPaymentTotals(
  filters: VendorPaymentFilters = {},
): Promise<VendorPaymentListTotals> {
  return fetchListTotals(
    "vendor_payment_list_totals",
    vendorPaymentTotalsArgs(filters),
    VENDOR_PAYMENT_TOTAL_FIELDS,
  );
}

/**
 * Deletes a vendor payment through the recalculating archive-routed RPC
 * (`xan_delete_vendor_payment`). The RPC locks the payment and its owning
 * purchase order, deletes the row, and recalculates PO/account balances in
 * the same transaction.
 *
 * Also reconciles any remainder `gl_vendor_credits` row this payment's
 * overpayment produced (linked via `gl_vendor_credits.source_vendor_payment_id`,
 * stamped by `xan_apply_split_payment_core`): an unapplied remainder credit
 * is hard-deleted in the same transaction; an applied one blocks the delete
 * (23514) until the applications are reversed. A legacy payment predating
 * the lineage column (`source_vendor_payment_id IS NULL`) matches no credit
 * and deletes exactly as before. See
 * openspec/specs/vendor-payments/spec.md's "Vendor credit lineage to its
 * originating payment" requirement.
 */
export async function deleteVendorPayment(
  id: string,
): Promise<{ success: boolean; deleted: boolean }> {
  const { error } = await backend.data.rpc("xan_delete_vendor_payment", {
    p_payment_id: id,
  });

  if (error) {
    throw new Error(error.message);
  }

  return { deleted: true, success: true };
}

/**
 * Corrects a recorded payment in place.
 *
 * The overpay ceiling is the database's:
 * `trg_xan_guard_gl_vendor_payments_overpay` fires on UPDATE and adds this
 * payment's current amount back into the purchase order's outstanding before
 * comparing, so raising the amount past the PO's capacity is rejected there. PO
 * totals and status are recalculated by trigger orchestration; this never
 * writes them. `uid` is left alone so
 * `xan_payments_trigger_generate_uid` can re-derive its date segment.
 *
 * The updated row is selected back because an `UPDATE` that matches nothing —
 * a payment deleted in another tab, or one an RLS policy hides — reports
 * `error === null` and zero rows. Treating that as success is worse than an
 * error on a money surface: the caller would report "payment updated", drop
 * the operator's edit, and navigate away from it.
 *
 * This does NOT reconcile a remainder `gl_vendor_credits` row the payment's
 * overpayment produced — only `xan_delete_vendor_payment` does, under the
 * credit's own lock. Reducing such a payment therefore leaves vendor credit
 * above the payment that justified it, which is why the edit surface warns and
 * points at delete-and-re-record for that case.
 */
export async function updateVendorPayment(
  id: string,
  input: VendorPaymentUpdateInput,
): Promise<void> {
  const { data, error } = await backend.data
    .from("gl_vendor_payments")
    .update({
      amount: input.amount,
      date: input.date,
      method: input.method,
      reference: input.reference,
    })
    .eq("id", id)
    .select("id")
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("Vendor payment not found. It may have been deleted.");
  }
}
